import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';

import { combineLatestWith } from 'rxjs/operators';
import { Observable, skip, take, withLatestFrom } from 'rxjs';

import { FeatureSwitchService } from '@common/modules/core/services/feature-switch/feature-switch.service';
import { FocusManagerService } from '@common/modules/accessibility/focus-manager.service';
import { FocusableComponent } from '@common/modules/accessibility/focusable/focusable.component';
import { CustomizationPage, FeatureSwitch } from '@common/modules/core/services/interfaces';
import { TranslateHelperService } from '@common/modules/translation/services/translate-helper.service';

import { CoreStoreService } from '../../../core/services/core-store.service';
import { SpeechPageStoreService } from '../../../customization-data/services/speech/speech-page-store.service';
import { resources } from './resources';
import { ISubMenuItem } from '../side-sub-menu/interfaces';
import { EdgeExtensionsStoreService } from '../../../core/services/edge-extensions-store.service';

@Component({
  selector: 'vi-customization-sub-menu',
  templateUrl: './customization-sub-menu.component.html',
  styleUrls: ['./customization-sub-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CustomizationSubMenuComponent extends FocusableComponent implements OnInit, OnDestroy {
  public menuItems: ISubMenuItem<CustomizationPage>[];
  public selectedPage$: Observable<CustomizationPage>;

  private readonly FOCUS_PAGE_CONTENT_TIMEOUT = 400;

  private resources = resources;
  private isSpeechEnabled$: Observable<boolean>;
  private hasEdgeExtensions$: Observable<boolean>;
  private focusPageContentTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private translateHelper: TranslateHelperService,
    private coreStoreService: CoreStoreService,
    private cdr: ChangeDetectorRef,
    private focusManager: FocusManagerService,
    private speechPageStoreService: SpeechPageStoreService,
    private featureSwitchService: FeatureSwitchService,
    private edgeExtensionsStoreService: EdgeExtensionsStoreService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.selectedPage$ = this.coreStoreService.getSelectedCustomizationPage();
    this.isSpeechEnabled$ = this.speechPageStoreService.isSpeechEnabled();
    this.hasEdgeExtensions$ = this.edgeExtensionsStoreService.hasEdgeExtensions$;

    this.translateHelper
      .translateResources(this.resources)
      .pipe(take(1), withLatestFrom(this.selectedPage$), combineLatestWith(this.isSpeechEnabled$, this.hasEdgeExtensions$))
      .subscribe(([[, selectedPage], isSpeechEnabled, hasEdgeExtensions]) => {
        this.initMenuOptions(selectedPage, isSpeechEnabled, hasEdgeExtensions);
        this.cdr.detectChanges();
        this.focusManager.focusByQuery(`.${selectedPage?.toLocaleLowerCase()}`, this.cdr);
      });

    this.selectedPage$
      .pipe(skip(1), combineLatestWith(this.isSpeechEnabled$, this.hasEdgeExtensions$))
      .subscribe(([newPage, isSpeechEnabled, hasEdgeExtensions]) => {
        this.initMenuOptions(newPage, isSpeechEnabled, hasEdgeExtensions);
        this.cdr.detectChanges();
      });
  }

  public handleSelectPage(item: ISubMenuItem<CustomizationPage>) {
    this.coreStoreService.navigateToCustomization(item.value);
    this.focusPageContent();
  }

  public ngOnDestroy(): void {
    this.clearFocusPageContentTimeout();
  }

  private focusPageContent(): void {
    this.clearFocusPageContentTimeout();
    this.focusPageContentTimeoutId = setTimeout(() => {
      this.focusPageContentTimeoutId = null;
      this.focusManager.focusByQuery('.main-content-action', this.cdr);
    }, this.FOCUS_PAGE_CONTENT_TIMEOUT);
  }

  private clearFocusPageContentTimeout(): void {
    if (!this.focusPageContentTimeoutId) {
      return;
    }

    clearTimeout(this.focusPageContentTimeoutId);
    this.focusPageContentTimeoutId = null;
  }

  private initMenuOptions(selectedPage: CustomizationPage, isSpeechEnabled: boolean, hasEdgeExtensions: boolean) {
    this.menuItems = [
      {
        value: CustomizationPage.Language,
        selected: selectedPage === CustomizationPage.Language,
        text: isSpeechEnabled ? this.resources.LanguageClassic : this.resources.TabsLanguage,
        class: CustomizationPage.Language?.toLocaleLowerCase()
      },
      {
        value: CustomizationPage.LanguageId,
        selected: selectedPage === CustomizationPage.LanguageId,
        text: this.resources.TabsLanguageID,
        class: CustomizationPage.LanguageId?.toLocaleLowerCase()
      },
      {
        value: CustomizationPage.People,
        selected: selectedPage === CustomizationPage.People,
        text: this.resources.TabsFaces,
        class: CustomizationPage.People?.toLocaleLowerCase()
      },
      {
        value: CustomizationPage.Brands,
        selected: selectedPage === CustomizationPage.Brands,
        text: this.resources.TabsBrands,
        class: CustomizationPage.Brands?.toLocaleLowerCase()
      }
    ];

    if (isSpeechEnabled) {
      const speechOption: ISubMenuItem<CustomizationPage> = {
        value: CustomizationPage.Speech,
        selected: selectedPage === CustomizationPage.Speech,
        text: this.resources.SpeechNew,
        class: CustomizationPage.Speech?.toLocaleLowerCase()
      };
      this.menuItems.unshift(speechOption);
    }

    if (this.featureSwitchService.featureSwitch(FeatureSwitch.LiveEdge) && hasEdgeExtensions) {
      const catalogOption: ISubMenuItem<CustomizationPage> = {
        value: CustomizationPage.Catalog,
        selected: selectedPage === CustomizationPage.Catalog,
        text: this.resources.LiveModelCatalogTitle,
        class: CustomizationPage.Catalog?.toLocaleLowerCase()
      };
      this.menuItems.unshift(catalogOption);
    }
  }
}
